import { useEffect, useState } from "react";
import { ListMusic, Loader2, Play, PlayCircle, SkipForward, Square, Trash2, X } from "lucide-react";
import { sameHeadline, wholeStory } from "@/lib/storyRead";

// Several stories lined up, read one after another.
export const ListenQueue = ({ rows, speaker, lang = "", onPlayAll, onRemove, onClear,
                             alsoTitles = {} }) => {
  const [at, setAt] = useState(() => Number(localStorage.getItem("cml_playat_queue")) || 0);

  // the queue remembers where you got to, even after a reload
  useEffect(() => {
    localStorage.setItem("cml_playat_queue", String(at >= rows.length ? 0 : at));
  }, [at, rows.length]);

  if (rows.length === 0) return null;
  const key = (story) => `${story.story_id}-read`;
  const busy = rows.some((s) => speaker.loading === key(s));
  const reading = rows.some((s) => speaker.playing === key(s));

  const playAll = () => onPlayAll();

  // one story, then silence — so someone can take them at their own pace
  const playOne = () => {
    const story = rows[Math.min(at, rows.length - 1)];
    speaker.play(key(story), wholeStory(story, lang), story.title);
    setAt((i) => (i + 1 >= rows.length ? 0 : i + 1));
  };

  return (
    <div className="mt-4 rounded-3xl border border-brand/30 bg-brand/[0.07] p-4" data-testid="listen-queue">
      <div className="flex flex-wrap items-center gap-2">
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-widest text-brand">
          <ListMusic className="h-4 w-4" /> Listen queue
          <span className="text-ink-soft">· {rows.length} lined up</span>
        </p>
        <button
          data-testid="queue-play-one"
          onClick={reading ? speaker.stop : playOne}
          title={reading ? "Stop" : `${rows[Math.min(at, rows.length - 1)]?.title || ""}`}
          className="ms-auto inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-brand/50 px-4 text-xs font-semibold text-brand transition-colors hover:bg-brand/10 active:scale-95"
        >
          {reading ? <Square className="h-3.5 w-3.5" /> : <PlayCircle className="h-3.5 w-3.5" />}
          {reading ? "Stop"
            : at === 0 || at >= rows.length ? "Play each one"
            : `Play the next one (${at + 1} of ${rows.length})`}
        </button>
        <button
          data-testid="queue-play"
          onClick={reading ? speaker.stop : playAll}
          className=" inline-flex min-h-[32px] items-center gap-1.5 rounded-full bg-brand px-4 text-xs font-semibold text-white transition-colors hover:bg-brand-hover active:scale-95"
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
            : reading ? <Square className="h-3.5 w-3.5" />
            : <Play className="h-3.5 w-3.5" />}
          {reading ? "Stop" : "Play them all"}
        </button>
        {reading && (
          <button
            data-testid="queue-skip"
            onClick={speaker.skip}
            className="inline-flex min-h-[32px] items-center gap-1.5 rounded-full border border-white/15 px-3 text-xs text-ink-soft transition-colors hover:border-brand/50 hover:text-brand"
          >
            <SkipForward className="h-3.5 w-3.5" /> Next
          </button>
        )}
        <button
          data-testid="queue-clear"
          onClick={() => { speaker.stop(); onClear(); }}
          aria-label="Empty the queue"
          className="rounded-full border border-white/10 p-1.5 text-ink-soft transition-colors hover:border-destructive/60 hover:text-destructive"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>

      <ol className="mt-2 space-y-1">
        {rows.map((story, i) => (
          <li key={story.story_id} className="flex items-start gap-2 text-xs"
              data-testid={`queue-row-${story.story_id}`}>
            <span className={`tabular-nums ${speaker.playing === key(story) ? "font-bold text-brand" : "text-ink-soft"}`}>
              {i + 1}.
            </span>
            <span className={speaker.playing === key(story) ? "text-brand" : ""} dir="auto">
              {story.title}
              <span className="text-ink-soft">{story.source ? ` · ${story.source}` : ""}</span>
              {alsoTitles[story.story_id]
                && !sameHeadline(alsoTitles[story.story_id], story.title) && (
                <span className="block italic text-ink-soft"
                      data-testid={`queue-also-${story.story_id}`}>
                  {alsoTitles[story.story_id]}
                </span>
              )}
            </span>
            <button
              data-testid={`queue-drop-${story.story_id}`}
              onClick={() => onRemove(story)}
              aria-label="Take it out of the queue"
              className="ms-auto text-ink-soft/70 hover:text-ink"
            >
              <X className="h-3 w-3" />
            </button>
          </li>
        ))}
      </ol>
    </div>
  );
};
